import React, { FC, ReactNode, RefObject, useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { NO_ACCESS_TOKEN } from "../@types/errors.types";
import { useErrorRedirect } from "../app/hooks/useErrorRedirect";
import { PageLayout } from "../components/Pages/PageLayout";
import { Loader } from "../loaders/loader.component";
import { CourseGrid } from "./course-grid";
import { CoursesHeader } from "./courses-header";
import { AssignedCourse } from "./courses.types";
import CustomerContext from "./customer.context";
import { useCourseFeed } from "./useCourseFeed";

type Params = {
  ltik: string;
};

type CourseFeedProps = {
  feedTrigger: React.RefObject<HTMLDivElement>;
  courses: AssignedCourse[];
};

const CourseFeed: FC<CourseFeedProps> = (props) => {
  const { courses, feedTrigger } = props;
  return (
    <>
      <CourseGrid courses={courses} />
      <div ref={feedTrigger} className="mt-12">
        &nbsp;
      </div>
    </>
  );
};

export const Courses: React.FC = () => {
  const { t } = useTranslation();
  const { ltik } = useParams<Params>();
  const navigate = useNavigate();
  if (!ltik) navigate(`/errors/${NO_ACCESS_TOKEN}`);
  localStorage.setItem("lti_app_token", ltik as string);

  const {
    courseFeed,
    isLoading,
    setSearchQuery,
    searchQuery,
    totalCourseCount,
    feedTriggerRef,
    hasError,
  } = useCourseFeed<HTMLDivElement>();

  useErrorRedirect(hasError);
  const customerDetails = useContext(CustomerContext);

  const title: ReactNode | string =
    searchQuery && totalCourseCount ? (
      <Trans
        i18nKey="courses.titleNCoursesFound"
        values={{ count: totalCourseCount }}
      />
    ) : searchQuery ? (
      t`courses.titleNoCoursesFound`
    ) : (
      t`courses.titleAllCourses`
    );

  // Determine which content to render
  let content: React.ReactNode;
  if (customerDetails?.isLtiV3DeeplinkingEnabled) {
    content = <p className="text-3xl">{t`courses.bodyDeeplinkEnabled`}</p>;
  } else if (isLoading) {
    content = <Loader />;
  } else {
    content = (
      <>
        <CoursesHeader
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          title={title}
        />
          <CourseFeed
            feedTrigger={feedTriggerRef as RefObject<HTMLDivElement>}
            courses={courseFeed}
          />
      </>
    );
  }
  return <PageLayout>{content}</PageLayout>;
};
